/**
 * ManoPaieskos Component (Employer Saved Searches)
 * 
 * Allows employers to save and manage candidate search criteria.
 * Similar to SavedSearches.tsx but for employer candidate searches.
 * 
 * @route /mano-paieskos
 */

import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { Navigate, useNavigate, Link } from 'react-router-dom';
import { Settings, Trash2, Search as SearchIcon, ChevronRight, Plus } from 'lucide-react';
import useConfirmDialog from '../hooks/useConfirmDialog';
import './ManoPaieskos.scss';

interface EmployerSavedSearch {
  id: number;
  title: string;
  // Search keywords
  keywords?: string[];
  // Personal Data
  location?: string[];
  age_range?: string;
  gender?: string;
  // Work Experience
  experience_years?: string;
  previous_companies?: string;
  positions?: string[];
  // Education
  education_level?: string[];
  field_of_study?: string[];
  // Skills
  technical_skills?: string[];
  languages?: string[];
  // Requirements
  desired_salary?: string;
  desired_work_type?: string[];
  desired_location?: string[];
  
  is_subscribed: boolean;
  total_results: number;
  new_results: number;
  created_at: string;
  updated_at: string;
}

interface SearchFilters {
  title: string;
  keywords: string[];
  location: string[];
  age_range: string;
  gender: string;
  experience_years: string;
  previous_companies: string;
  positions: string[];
  education_level: string[];
  field_of_study: string[];
  technical_skills: string[];
  languages: string[];
  desired_salary: string;
  desired_work_type: string[];
  desired_location: string[];
  is_subscribed: boolean;
}

const ManoPaieskos: React.FC = () => {
  const { user, isAuthenticated, isLoading } = useAuth();
  const { showToast } = useToast();
  const { confirm, ConfirmDialogNode } = useConfirmDialog();
  const navigate = useNavigate();
  const [savedSearches, setSavedSearches] = useState<EmployerSavedSearch[]>([]);
  const [loading, setLoading] = useState(true);
  const [allCVs, setAllCVs] = useState<any[]>([]);
  const [loadingCVs, setLoadingCVs] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingSearch, setEditingSearch] = useState<EmployerSavedSearch | null>(null);
  const [selectedSearches, setSelectedSearches] = useState<number[]>([]);
  const [searchTitleFilter, setSearchTitleFilter] = useState('');
  const [searchFilters, setSearchFilters] = useState<SearchFilters>({
    title: '',
    keywords: [],
    location: [],
    age_range: '',
    gender: '',
    experience_years: '',
    previous_companies: '',
    positions: [],
    education_level: [],
    field_of_study: [],
    technical_skills: [],
    languages: [],
    desired_salary: '',
    desired_work_type: [],
    desired_location: [],
    is_subscribed: true
  });

  useEffect(() => {
    if (isAuthenticated && user?.user_type === 'employer') {
      fetchSavedSearches();
      fetchAllCVs();
    }
  }, [isAuthenticated, user]);


  const fetchSavedSearches = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('auth_token');
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

      const response = await fetch(`${API_URL}/employer-saved-searches/my`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const result = await response.json();
        setSavedSearches(result.data || []);
      }
    } catch (error) {
      console.error('Error fetching saved searches:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAllCVs = async () => {
    try {
      setLoadingCVs(true);
      const token = localStorage.getItem('auth_token');
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

      const response = await fetch(`${API_URL}/cv/employer/visible`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const result = await response.json();
        setAllCVs(result.data || result || []);
      }
    } catch (error) {
      console.error('Error fetching CVs:', error);
    } finally {
      setLoadingCVs(false);
    }
  };

  const countMatchingCVs = (search: EmployerSavedSearch): number => {
    return allCVs.filter(cv => {
      // Location filter
      if (search.location && search.location.length > 0) {
        const cvLocation = cv.location?.toLowerCase() || '';
        const matchesLocation = search.location.some(city => 
          city && cvLocation.includes(city.toLowerCase())
        );
        if (!matchesLocation) return false;
      }

      // Add more filters as needed
      return true;
    }).length;
  };

  const countNewCVs = (search: EmployerSavedSearch): number => {
    const matchingCVs = allCVs.filter(cv => {
      // Location filter
      if (search.location && search.location.length > 0) {
        const cvLocation = cv.location?.toLowerCase() || '';
        const matchesLocation = search.location.some(city => 
          city && cvLocation.includes(city.toLowerCase())
        );
        if (!matchesLocation) return false;
      }
      return true;
    });

    // Count CVs created in last 7 days
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
    
    return matchingCVs.filter(cv => {
      if (cv.created_at) {
        const cvCreatedDate = new Date(cv.created_at);
        return cvCreatedDate >= sevenDaysAgo;
      }
      return false;
    }).length;
  };

  const handleCreateSearch = () => {
    setEditingSearch(null);
    setSearchFilters({
      title: '',
      keywords: [],
      location: [],
      age_range: '',
      gender: '',
      experience_years: '',
      previous_companies: '',
      positions: [],
      education_level: [],
      field_of_study: [],
      technical_skills: [],
      languages: [],
      desired_salary: '',
      desired_work_type: [],
      desired_location: [],
      is_subscribed: true
    });
    setShowCreateModal(true);
  };

  const handleEditSearch = (search: EmployerSavedSearch) => {
    setEditingSearch(search);
    setSearchFilters({
      title: search.title,
      keywords: search.keywords || [],
      location: search.location || [],
      age_range: search.age_range || '',
      gender: search.gender || '',
      experience_years: search.experience_years || '',
      previous_companies: search.previous_companies || '',
      positions: search.positions || [],
      education_level: search.education_level || [],
      field_of_study: search.field_of_study || [],
      technical_skills: search.technical_skills || [],
      languages: search.languages || [],
      desired_salary: search.desired_salary || '',
      desired_work_type: search.desired_work_type || [],
      desired_location: search.desired_location || [],
      is_subscribed: search.is_subscribed
    });
    setShowCreateModal(true);
  };

  const handleSaveSearch = async () => {
    if (!searchFilters.title.trim()) {
      return;
    }

    try {
      const token = localStorage.getItem('auth_token');
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
      
      const payload = {
        title: searchFilters.title,
        keywords: searchFilters.keywords.length > 0 ? searchFilters.keywords : null,
        location: searchFilters.location.length > 0 ? searchFilters.location : null,
        age_range: searchFilters.age_range || null,
        gender: searchFilters.gender || null,
        experience_years: searchFilters.experience_years || null,
        previous_companies: searchFilters.previous_companies || null,
        positions: searchFilters.positions.length > 0 ? searchFilters.positions : null,
        education_level: searchFilters.education_level.length > 0 ? searchFilters.education_level : null,
        field_of_study: searchFilters.field_of_study.length > 0 ? searchFilters.field_of_study : null,
        technical_skills: searchFilters.technical_skills.length > 0 ? searchFilters.technical_skills : null,
        languages: searchFilters.languages.length > 0 ? searchFilters.languages : null,
        desired_salary: searchFilters.desired_salary || null,
        desired_work_type: searchFilters.desired_work_type.length > 0 ? searchFilters.desired_work_type : null,
        desired_location: searchFilters.desired_location.length > 0 ? searchFilters.desired_location : null,
        is_subscribed: searchFilters.is_subscribed
      };

      const url = editingSearch 
        ? `${API_URL}/employer-saved-searches/${editingSearch.id}`
        : `${API_URL}/employer-saved-searches`;

      const method = editingSearch ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        setShowCreateModal(false);
        fetchSavedSearches();
      }
    } catch (error) {
      console.error('Error saving search:', error);
    }
  };

  const handleDeleteSearch = async (id: number) => {
    try {
      const token = localStorage.getItem('auth_token');
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

      const response = await fetch(`${API_URL}/employer-saved-searches/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        fetchSavedSearches();
      }
    } catch (error) {
      console.error('Error deleting search:', error);
    }
  };

  const handleGoToSearch = (search: EmployerSavedSearch) => {
    // Navigate to candidate search (Skelbimai) with these filters
    const params = new URLSearchParams();
    params.set('mode', 'employer');
    
    // Use saved keywords, or fall back to positions/title as search terms
    const searchKeywords: string[] = [];
    if (search.keywords && search.keywords.length > 0) {
      searchKeywords.push(...search.keywords);
    }
    if (search.positions && search.positions.length > 0) {
      searchKeywords.push(...search.positions);
    }
    // If still no keywords, use the search title
    if (searchKeywords.length === 0 && search.title) {
      searchKeywords.push(search.title);
    }
    
    if (searchKeywords.length > 0) params.set('keywords', searchKeywords.join(','));
    if (search.location && search.location.length > 0) params.set('location', search.location.join(','));

    navigate(`/skelbimai?${params.toString()}`);
  };

  const toggleMultiSelect = (field: keyof SearchFilters, value: string) => {
    const current = searchFilters[field] as string[];
    if (current.includes(value)) {
      setSearchFilters({
        ...searchFilters,
        [field]: current.filter(v => v !== value)
      });
    } else {
      setSearchFilters({
        ...searchFilters,
        [field]: [...current, value]
      });
    }
  };

  const formatCriteria = (search: EmployerSavedSearch): string => {
    const criteria: string[] = [];

    if (search.keywords && search.keywords.length > 0) {
      criteria.push(`Raktažodžiai: ${search.keywords.join(', ')}`);
    }
    if (search.positions && search.positions.length > 0) {
      criteria.push(`Pareigos: ${search.positions.join(', ')}`);
    }
    if (search.gender) {
      criteria.push(search.gender === 'male' ? 'Vyras' : 'Moteris');
    }
    if (search.age_range) {
      criteria.push(`${search.age_range} metų`);
    }
    if (search.experience_years) {
      criteria.push(`Patirtis: ${search.experience_years}`);
    }
    if (search.location && search.location.length > 0) {
      criteria.push(search.location.join(', '));
    }

    return criteria.length > 0 ? criteria.join(', ') : 'Visi kriterijai';
  };

  const handleToggleSelectAll = () => {
    const filteredSearches = filterSearchesByTitle(savedSearches);
    
    if (selectedSearches.length === filteredSearches.length && filteredSearches.length > 0) {
      setSelectedSearches([]);
    } else {
      setSelectedSearches(filteredSearches.map(s => s.id));
    }
  };

  const handleToggleSelect = (id: number) => {
    setSelectedSearches(prev => 
      prev.includes(id) ? prev.filter(sid => sid !== id) : [...prev, id]
    );
  };

  const handleDeleteSelected = async () => {
    if (selectedSearches.length === 0) return;

    if (!await confirm(`Ar tikrai norite ištrinti ${selectedSearches.length} paieškų? Šio veiksmo negalima atšaukti.`)) return;

    try {
      const token = localStorage.getItem('auth_token');
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

      await Promise.all(
        selectedSearches.map(id =>
          fetch(`${API_URL}/employer-saved-searches/${id}`, {
            method: 'DELETE',
            headers: {
              'Authorization': `Bearer ${token}`
            }
          })
        )
      );

      setSelectedSearches([]);
      fetchSavedSearches();
    } catch (error) {
      console.error('Error deleting searches:', error);
      showToast('Įvyko klaida trinant paieškas', 'error');
    }
  };

  const filterSearchesByTitle = (searches: EmployerSavedSearch[]) => {
    if (!searchTitleFilter.trim()) return searches;
    
    const filterLower = searchTitleFilter.toLowerCase().trim();
    return searches.filter(search => 
      search.title.toLowerCase().includes(filterLower)
    );
  };

  // Show loading state while auth is being checked
  if (isLoading) {
    return (
      <div className="mano-paieskos-container">
        <div className="loading">Kraunama...</div>
      </div>
    );
  }

  // Redirect if not authenticated or not an employer
  if (!isAuthenticated || user?.user_type !== 'employer') {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="mano-paieskos-page">
      {ConfirmDialogNode}
      <div className="container">
        <div className="header-row">
          <div className="breadcrumbs">
            <Link to="/">Titulinis</Link>
            <ChevronRight size={14} />
            <span>Mano paieškos</span>
          </div>
          <div className="filters-toolbar">
            <div className="select-all-dropdown">
              <button 
                className="select-all-btn"
                onClick={handleToggleSelectAll}
              >
                <input 
                  type="checkbox" 
                  checked={selectedSearches.length > 0 && selectedSearches.length === filterSearchesByTitle(savedSearches).length}
                  onChange={handleToggleSelectAll}
                  onClick={(e) => e.stopPropagation()}
                />
                <span>Žymėti visus</span>
              </button>
            </div>
            <button 
              className="btn-delete"
              onClick={handleDeleteSelected}
              disabled={selectedSearches.length === 0}
            >
              <Trash2 size={16} />
              <span>Trinti</span>
            </button>
            <div className="search-input-wrapper">
              <input 
                type="text"
                className="search-title-input"
                placeholder="Paieškos pavadinimas"
                value={searchTitleFilter}
                onChange={(e) => setSearchTitleFilter(e.target.value)}
              />
              <SearchIcon size={16} className="search-icon" />
            </div>
            <button className="btn-new-search" onClick={handleCreateSearch}>
              <Plus size={16} />
              <span>Nauja paieška</span>
            </button>
          </div>
        </div>

        <div className="saved-section">
          {loading || loadingCVs ? (
            <div className="loading">Kraunama...</div>
          ) : savedSearches.length === 0 ? (
            <div className="empty-state">
              <SearchIcon size={48} />
              <p>Dar neturite išsaugotų paieškų</p>
              <button className="btn-primary" onClick={handleCreateSearch}>
                Sukurti pirmą paiešką
              </button>
            </div>
          ) : (
            <div className="searches-grid">
                  {filterSearchesByTitle(savedSearches).map((search) => (
                    <div key={search.id} className={`search-card ${selectedSearches.includes(search.id) ? 'selected' : ''}`}>
                      <div className="search-card-header">
                        <div className="checkbox-wrapper">
                          <input
                            type="checkbox"
                            checked={selectedSearches.includes(search.id)}
                            onChange={() => handleToggleSelect(search.id)}
                            onClick={(e) => e.stopPropagation()}
                          />
                        </div>
                        <div className="header-actions">
                          <button
                            className="btn-icon"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEditSearch(search);
                            }}
                            title="Nustatymai"
                          >
                            <Settings size={18} />
                          </button>
                          <button
                            className="btn-icon"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteSearch(search.id);
                            }}
                            title="Ištrinti"
                          >
                            <Trash2 size={18} />
                          </button>
                        </div>
                      </div>

                      <div className="search-card-body" onClick={() => handleGoToSearch(search)}>
                        <h3 className="search-title">{search.title}</h3>
                        <p className="search-criteria">
                          <span className="criteria-label">Kriterijai:</span><br />
                          {formatCriteria(search)}
                        </p>
                      </div>

                      <div className="search-card-footer">
                        <span className="result-count">
                          Iš viso: <strong>{countMatchingCVs(search)}</strong>
                        </span>
                        <span className="new-count">
                          Naujų: <span className="badge-new">{countNewCVs(search)}</span>
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
          )}
        </div>
      </div>

      {/* Create/Edit Modal */}
      {showCreateModal && (
        <div className="modal-overlay">
          <div className="modal-content large-modal">
            <div className="modal-header">
              <h2>{editingSearch ? 'Redaguoti paiešką' : 'Nauja paieška'}</h2>
              <button className="btn-close" onClick={() => setShowCreateModal(false)}>&times;</button>
            </div>

            <div className="modal-body">
              <div className="form-group">
                <label htmlFor="searchTitle">Paieškos pavadinimas *</label>
                <input
                  type="text"
                  id="searchTitle"
                  value={searchFilters.title}
                  onChange={(e) => setSearchFilters({ ...searchFilters, title: e.target.value })}
                  placeholder="pvz., Eksporto vadovas"
                />
              </div>

              <div className="form-group">
                <label htmlFor="searchKeywords">Paieškos raktažodžiai (atskirti kableliais)</label>
                <input
                  type="text"
                  id="searchKeywords"
                  value={searchFilters.keywords.join(', ')}
                  onChange={(e) => setSearchFilters({ 
                    ...searchFilters, 
                    keywords: e.target.value.split(',').map(k => k.trim()).filter(k => k)
                  })}
                  placeholder="pvz., programuotojas, vadybininkas, buhalteris"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Miestas</label>
                  <div className="checkbox-grid">
                    {['Vilnius', 'Kaunas', 'Klaipėda', 'Šiauliai', 'Panevėžys'].map(city => (
                      <label key={city} className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={searchFilters.location.includes(city)}
                          onChange={() => toggleMultiSelect('location', city)}
                        />
                        <span>{city}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div className="form-group">
                  <label>Lytis</label>
                  <div className="checkbox-grid">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={searchFilters.gender === 'male'}
                        onChange={() => setSearchFilters({ ...searchFilters, gender: searchFilters.gender === 'male' ? '' : 'male' })}
                      />
                      <span>Vyras</span>
                    </label>
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={searchFilters.gender === 'female'}
                        onChange={() => setSearchFilters({ ...searchFilters, gender: searchFilters.gender === 'female' ? '' : 'female' })}
                      />
                      <span>Moteris</span>
                    </label>
                  </div>
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="experience">Patirties metai</label>
                <input
                  type="text"
                  id="experience"
                  value={searchFilters.experience_years}
                  onChange={(e) => setSearchFilters({ ...searchFilters, experience_years: e.target.value })}
                  placeholder="pvz., 3-5"
                />
              </div>

              <div className="form-group checkbox-group">
                <label>
                  <input
                    type="checkbox"
                    checked={searchFilters.is_subscribed}
                    onChange={(e) => setSearchFilters({ ...searchFilters, is_subscribed: e.target.checked })}
                  />
                  Prenumeruoti šią paiešką (gauti pranešimus apie naujus kandidatus)
                </label>
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setShowCreateModal(false)}>
                Atšaukti
              </button>
              <button 
                className="btn-primary" 
                onClick={handleSaveSearch}
                disabled={!searchFilters.title.trim()}
              >
                {editingSearch ? 'Atnaujinti' : 'Išsaugoti'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManoPaieskos;

